<template>
  <tr class="invoices-row">
    <td colspan="9" class="p-0">
      <div class="invoices-container p-3 bg-light border-top">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center mb-3">
          <h6 class="mb-0">Faktury k objednávce</h6>
          <div class="text-muted small">ID verze: {{ versionId }}</div>
        </div>

        <!-- Loading indikátor -->
        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border spinner-border-sm text-primary" role="status">
            <span class="visually-hidden">Načítání...</span>
          </div>
          <div class="mt-2 text-muted small">Načítám seznam faktur...</div>
        </div>

        <!-- Seznam faktur (vizuálně jako verze kalkulace) -->
        <div v-else-if="invoices.length > 0" class="invoices-list">
          <div
              v-for="invoice in invoices"
              :key="invoice.id"
              class="invoice-item p-3 mb-2 border rounded bg-white"
          >
            <div class="row align-items-center">
              <!-- Číslo a typ -->
              <div class="col-md-5">
                <div v-if="invoice.status !== 'deleted'" class="fw-bold">
                  {{ invoice.invoice_number }}
                </div>
                <small class="text-muted">{{ getInvoiceName(invoice.document_type) }}</small>
              </div>

              <!-- Částka -->
              <div class="col-md-2 text-start">
                <div>
                  {{ formatCurrency(invoice.price_without_vat) }} / <span
                    class="fw-bold">{{ formatCurrency(invoice.price_final) }}</span>
                </div>
                <small class="text-muted">Sazba DPH: {{ invoice.vat_rate }}%</small>

              </div>

              <!-- Splatnost a vytvoření -->
              <div class="col-md-2 text-start">
                <div>
                  <small class="text-muted">Splatnost: </small>
                  <small>{{ formatDate(invoice.due_date) }}</small>
                </div>
                <div v-if="invoice.created_at">
                  <small class="text-muted">Vytvořeno: </small>
                  <small>{{ formatDate(invoice.created_at) }}</small>
                </div>
              </div>

              <!-- Status -->
              <div class="col-md-2 text-start">
                <span class="badge" :class="getStatusBadgeClass(invoice.status)">
                  {{ getStatusBadgeName(invoice.status) }}
                </span>
              </div>

              <!-- Akční tlačítka -->
              <div class="col-md-1 text-end">
                <div class="d-flex justify-content-end">
                  <BaseActionIconButton
                      title="Zobrazit fakturu"
                      icon="bi-eye"
                      variant="primary"
                      @click="invoice.status !== 'deleted'? viewInvoice(invoice.public_html) : viewInvoice(invoice.html_url)"
                  />
                  <BaseActionIconButton
                      title="Aktualizovat fakturu dle Fakturoidu"
                      icon="bi bi-download"
                      variant="secondary"
                      @click="fakturoidInvoiceSync(invoice.id)"
                      :disabled="invoice.status === 'deleted'"
                  />
                  <BaseActionIconButton
                      title="Stornovat fakturu"
                      icon="bi-trash3-fill"
                      variant="danger"
                      @click="deleteInvoice(invoice.id)"
                      :disabled="!(['open', 'overdue'].includes(invoice.status))"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pokud nejsou žádné faktury -->
        <div v-else class="text-center py-4 text-muted bg-white border rounded">
          <i class="bi bi-info-circle me-2"></i>
          Žádné faktury nebyly nalezeny.
        </div>
      </div>
    </td>
  </tr>
</template>

<script setup>
import {ref, onMounted, defineProps, defineExpose, watch} from 'vue';
import myApi from "@/api/api";
import BaseActionIconButton from "@/components/base/BaseActionIconButton.vue";
import {showPdfFile} from "@/common/functions";

const props = defineProps({
  versionId: {
    type: [Number, String],
    required: true
  },
  showDeleted: {
    type: Boolean,
    default: false
  }
});

const invoices = ref([]);
const loading = ref(false);

const loadInvoices = async () => {
  loading.value = true;
  try {
    // Volání api funkce s parametrem deleted = null pro zobrazení všech
    const deletedParam = props.showDeleted ? null : false;
    invoices.value = await myApi.invoicesByVersionList(props.versionId, deletedParam);
  } catch (error) {
    console.error("Chyba při načítání faktur:", error);
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('cs-CZ');
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return '- Kč';
  return new Intl.NumberFormat('cs-CZ').format(value) + ' Kč';
};

const getStatusBadgeClass = (status) => {
  const statusMap = {
    'paid': 'text-bg-success',
    'cancelled': 'text-bg-danger',
    'deleted': 'text-bg-danger',
    'overdue': 'text-bg-warning',
    'sent': 'text-bg-info',
    'draft': 'text-bg-secondary',
  };
  return statusMap[status?.toLowerCase()] || 'text-bg-light border';
};

const getInvoiceName = (documentType) => {
  if (!documentType) return 'Neznámý typ faktury';
  const typeMap = {
    'invoice': 'Faktura',
    'proforma': 'Zálohová faktura',
    'final_invoice': 'Finální faktura',
  };
  return typeMap[documentType] || 'Neznámý typ faktury';
}

const getStatusBadgeName = (status) => {
  if (!status) return 'Neznámý status';
  const statusMap = {
    'open': 'Vystavená',
    'sent': 'Odeslaná',
    'overdue': 'Po splatnosti',
    'paid': 'Uhrazená',
    'cancelled': 'Stornována',
    'deleted': 'Smazaná',
  };
  return statusMap[status] || 'Neznámý status';
}

const viewInvoice = (urlPdf) => showPdfFile(urlPdf);
const fakturoidInvoiceSync = async (invoiceId) => {
  await myApi.fakturoidInvoiceSync(invoiceId);
  await loadInvoices();
}

const deleteInvoice = async (invoiceId) => {
  if (confirm("Opravdu si přejete fakturu smazat? Operace je nenávratná!")) {
    await myApi.deleteInvoice(invoiceId);
    await loadInvoices();
  }
}

watch(() => props.showDeleted, async () => {
  await loadInvoices();
});

// Expose pro možnost refreshování z rodiče
defineExpose({
  loadInvoices
});

onMounted(() => {
  loadInvoices();
});
</script>

<style scoped>
.invoices-container {
  border-left: 4px solid #0d6efd;
}

.invoice-item {
  transition: all 0.2s ease;
}

.invoice-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-color: #adb5bd !important;
}

.badge {
  font-weight: 500;
  padding: 0.5em 0.8em;
}
</style>